import { z } from 'zod';

/** Test vs live data/keys are fully separate per tenant (MASTER_PROMPT.md §4). */
export const modeSchema = z.enum(['test', 'live']);
export type Mode = z.infer<typeof modeSchema>;

/** MASTER_PROMPT.md §5 */
export const settlementModeSchema = z.enum(['DIRECT', 'DESTINATION', 'PLATFORM_HELD']);
export type SettlementMode = z.infer<typeof settlementModeSchema>;

export const paymentProviderSchema = z.enum(['stripe', 'paypal']);
export type PaymentProviderName = z.infer<typeof paymentProviderSchema>;

export const feePayerSchema = z.enum(['BUYER', 'CLIENT', 'SPLIT']);
export type FeePayer = z.infer<typeof feePayerSchema>;

export const orderStatusSchema = z.enum([
  'pending',
  'paid',
  'fulfilled',
  'partially_refunded',
  'refunded',
  'cancelled',
  'disputed',
]);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

export const ticketStatusSchema = z.enum([
  'issued',
  'checked_in',
  'transferred',
  'refunded',
  'cancelled',
  'void',
]);
export type TicketStatus = z.infer<typeof ticketStatusSchema>;

export const integrationChannelSchema = z.enum([
  'api',
  'hosted_checkout',
  'widget',
  'dashboard',
  'custom_frontend',
]);
export type IntegrationChannel = z.infer<typeof integrationChannelSchema>;

export const deliveryTimingSchema = z.enum(['immediate', 'delayed', 'scheduled_release']);
export type DeliveryTiming = z.infer<typeof deliveryTimingSchema>;

export const notificationChannelSchema = z.enum(['email', 'sms']);
export type NotificationChannel = z.infer<typeof notificationChannelSchema>;

export const planComponentTypeSchema = z.enum([
  'per_transaction',
  'subscription',
  'tiered_volume',
  'per_event',
  'community',
  'hybrid',
  'prepaid_credits',
  'custom_deal',
  'pass_through',
]);
export type PlanComponentType = z.infer<typeof planComponentTypeSchema>;

export const invoiceStatusSchema = z.enum([
  'draft',
  'open',
  'paid',
  'overdue',
  'void',
  'uncollectible',
]);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const payoutStatusSchema = z.enum([
  'pending',
  'approved',
  'in_transit',
  'paid',
  'failed',
  'cancelled',
]);
export type PayoutStatus = z.infer<typeof payoutStatusSchema>;

export const ledgerAccountTypeSchema = z.enum([
  'tenant_balance', // amount Loctopus owes a tenant (PLATFORM_HELD)
  'platform_revenue', // Loctopus fee revenue
  'provider_fees', // Stripe/PayPal fees (contra)
  'tax_payable', // GST collected on Loctopus invoices
  'buyer_clearing', // in-flight funds between charge and settlement
  'prepaid_credits', // tenant prepaid balance
  'reserve_holdback', // withheld reserve per payout policy
]);
export type LedgerAccountType = z.infer<typeof ledgerAccountTypeSchema>;

export const ledgerEntryTypeSchema = z.enum([
  'sale',
  'refund',
  'chargeback',
  'platform_fee',
  'provider_fee',
  'payout',
  'invoice_charge',
  'credit_topup',
  'credit_draw',
  'correction',
]);
export type LedgerEntryType = z.infer<typeof ledgerEntryTypeSchema>;
